import { ForwardedRef, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import styles from './ExpressionInput.module.scss';
import { createEditor, Editor, Transforms, Range, Text } from 'slate'
import { Slate, Editable, withReact, RenderLeafProps, RenderElementProps, ReactEditor, RenderPlaceholderProps } from 'slate-react'
import { withMentions, Element, Leaf, insertMention, Portal, createMention } from './ExpressionSlateComponents';
import { useTranslation } from 'react-i18next';
import ExpressionSystem, { ExpressionParameter } from './ExpressionSystem';
import { BODY_CLICK_IGNORE_CLASS_NAME } from 'screens/business/budgets/budget/lines/utils';

const serializeAll = (nodes: any[]) => {
    return nodes.map(serialize).join('');
}

const serialize = (node: any) => {
    if (Text.isText(node)) {
        return node.text
    }

    switch (node.type) {
        case 'mention':
            return ExpressionSystem.serializeParameterMentionId(node.id);
        default:
            return node.children.map((n: any) => serialize(n)).join('');
    }
}

const deserialize = (expression: string, parameters: ExpressionParameter[]) => {
    const regex = /M_[^,)]*_E/g;

    const result: string[] = [];
    let currentText = '';
    let i = 0;
    let inM = false;

    while (i < expression.length) {
        if (expression[i] === 'M' && expression[i + 1] === '_') {
            // If we are in the middle of processing other text, push it
            if (currentText) {
                result.push(currentText);
                currentText = '';
            }
            inM = true;
            currentText += expression[i] + expression[i + 1]; // Add 'M'
            i += 2; // Skip 'M_'
        } else if (inM && expression[i] === '_' && expression[i + 1] === 'E') {
            currentText += '_E'; // Add '_E' part
            result.push(currentText); // Push the "M_..._E" to the result
            currentText = ''; // Reset for the next part
            i += 2; // Skip '_E'
            inM = false;
        } else {
            currentText += expression[i]; // Add normal character to currentText
            i++;
        }
    }

    // If there's any leftover text, push it at the end
    if (currentText) {
        result.push(currentText);
    }

    const children = result.map(x => {
        if (x.match(regex)) {
            const parameter = parameters.find(p => ExpressionSystem.serializeParameterMentionId(p.id) === x);
            if (!parameter) {
                return { text: 'NOT_FOUND_ERROR' };
            }
            return createMention(parameter.name, parameter.id);
        }

        return { text: x }
    });

    children.push({ text: '' });

    const tree = [
        {
            type: 'paragraph',
            children,
        }
    ];

    return tree;
}

export interface ExpressionInputRef {
    updateValue: (newValue: string) => void;
}

export interface ExpressionInputProps {
    value: string
    disabled?: boolean;
    parameters: ExpressionParameter[];
    onChange: (expression: string | null) => void;
}

const ExpressionInput = ({
    value,
    parameters,
    disabled,
    onChange,
}: ExpressionInputProps, selfRef: ForwardedRef<ExpressionInputRef>) => {
    const { t } = useTranslation();
    const ref = useRef<HTMLDivElement | null>(null);
    const [target, setTarget] = useState<Range | null>(null);
    const [index, setIndex] = useState(0);
    const [search, setSearch] = useState('');
    const renderElement = useCallback(
        (props: RenderElementProps) => <Element {...props} />,
        []
    );
    const renderLeaf = useCallback(
        (props: RenderLeafProps) => <Leaf {...props} />,
        []
    );
    const editor = useMemo(
        () => withMentions(withReact(createEditor())),
        []
    );

    function updateValue(newValue: string) {
        const newExpressionTree = deserialize(newValue, parameters)

        Transforms.delete(editor, { at: [0] });
        Transforms.insertNodes(editor, newExpressionTree, { at: [0] });
    }

    useImperativeHandle(selfRef, () => ({
        updateValue,
    }));

    const chars = parameters.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase())
    ); // .slice(0, 10)

    const onKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (target && chars.length > 0) {
                switch (event.key) {
                    case 'ArrowDown':
                        event.preventDefault()
                        setIndex((_prevIndex) => {
                            const nextIndex = _prevIndex >= chars.length - 1 ? 0 : _prevIndex + 1;
                            scrollIntoView(nextIndex);
                            return nextIndex;
                        });
                        break
                    case 'ArrowUp':
                        event.preventDefault()
                        setIndex((_prevIndex) => {
                            const prevIndex = _prevIndex <= 0 ? chars.length - 1 : _prevIndex - 1;
                            scrollIntoView(prevIndex);
                            return prevIndex;
                        });
                        break
                    case 'Tab':
                    case 'Enter':
                        event.preventDefault()
                        Transforms.select(editor, target as any)
                        insertMention(editor, chars[index].name, chars[index].id)
                        setTarget(null)
                        break
                    case 'Escape':
                        event.preventDefault()
                        setTarget(null)
                        break
                }
            }
        },
        [chars, editor, index, target]
    );

    const scrollIntoView = (selectedIndex: number) => {
        if (!ref.current) return;
        const suggestionItems = ref.current.querySelectorAll(`.${styles.suggestionItem}`);
        if (suggestionItems[selectedIndex]) {
            suggestionItems[selectedIndex].scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    };

    useEffect(() => {
        if (target && chars.length > 0 && ref.current) {
            const el = ref.current
            const domRange = ReactEditor.toDOMRange(editor, target as any)
            const rect = domRange.getBoundingClientRect()
            el.style.top = `${rect.top + window.pageYOffset + 24}px`
            el.style.left = `${rect.left + window.pageXOffset}px`
        }
    }, [chars.length, editor, index, search, target])

    return (
        <div className={styles.inputWrapper}>
            <Slate
                editor={editor}
                initialValue={deserialize(value, parameters)}
                onChange={(nodes) => {
                    const { selection } = editor

                    // if (selection && Range.isCollapsed(selection)) {
                    //     const [start] = Range.edges(selection)

                    //     const wordBefore = Editor.before(editor, start, { unit: 'word' })
                    //     const before = wordBefore && Editor.before(editor, wordBefore)
                    //     const beforeRange = before && Editor.range(editor, before, start)
                    //     const beforeText = beforeRange && Editor.string(editor, beforeRange)
                    //     const beforeMatch = beforeText && beforeText.match(/^@(\w+)$/)

                    //     const after = Editor.after(editor, start)
                    //     const afterRange = Editor.range(editor, start, after)
                    //     const afterText = Editor.string(editor, afterRange)
                    //     const afterMatch = afterText.match(/^(\s|$)/)

                    //     if (beforeMatch && afterMatch) {
                    //         setTarget(beforeRange)
                    //         setSearch(beforeMatch[1])
                    //         setIndex(0)
                    //         return
                    //     }
                    // }

                    // if (selection && Range.isCollapsed(selection)) {
                    //     const [start] = Range.edges(selection);

                    //     // Scan backward to find the '@' and capture the full mention
                    //     let before = start;
                    //     let beforeText = '';
                    //     while (true) {
                    //         const charBefore = Editor.before(editor, before, { unit: 'character' });
                    //         if (!charBefore) break;

                    //         const charRange = Editor.range(editor, charBefore, before);
                    //         const char = Editor.string(editor, charRange);

                    //         // Stop scanning backward if we reach '@' or a line break
                    //         if (char === '@' || char === '\n') break;

                    //         before = charBefore;
                    //         beforeText = char + beforeText;
                    //     }

                    //     // Ensure we found '@' before the mention
                    //     if (!Editor.before(editor, before, { unit: 'character' })) return;

                    //     const beforeRange = Editor.range(editor, before, start);
                    //     const searchQuery = beforeText.trim(); // Trim spaces

                    //     // Get the text after cursor (allow spaces)
                    //     const after = Editor.after(editor, start);
                    //     const afterRange = Editor.range(editor, start, after);
                    //     const afterText = Editor.string(editor, afterRange);

                    //     // Allow spaces after mentions
                    //     const afterMatch = afterText.match(/^(\s|$)/) || afterText === '';

                    //     if (afterMatch) {
                    //         setTarget(beforeRange);
                    //         setSearch(searchQuery);
                    //         setIndex(0);
                    //         return;
                    //     }
                    // }

                    if (selection && Range.isCollapsed(selection)) {
                        const [start] = Range.edges(selection);

                        // Move backwards to find '@'
                        let before = start;
                        let mentionText = '';
                        while (true) {
                            const charBefore = Editor.before(editor, before, { unit: 'character' });
                            if (!charBefore) break;

                            const charRange = Editor.range(editor, charBefore, before);
                            const char = Editor.string(editor, charRange);

                            before = charBefore;
                            mentionText = char + mentionText;

                            if (char === '@') {
                                break;
                            }
                        }

                        // Ensure we found a valid '@' mention
                        if (mentionText.startsWith('@')) {
                            const beforeRange = Editor.range(editor, before, start);
                            const searchQuery = mentionText.slice(1); // Remove '@' from search query

                            // Get the text after cursor
                            const after = Editor.after(editor, start);
                            const afterRange = Editor.range(editor, start, after);
                            const afterText = Editor.string(editor, afterRange);

                            // Ensure we're at the end of the mention
                            const afterMatch = afterText.match(/^(\s|$)/) || afterText === '';

                            if (afterMatch) {
                                setTarget(beforeRange);
                                setSearch(searchQuery);
                                setIndex(0);
                                return;
                            }
                        }
                    }

                    setTarget(null);

                    const newValue = serializeAll(nodes);
                    onChange(newValue);
                }}
            >
                <Editable
                    renderElement={renderElement}
                    renderLeaf={renderLeaf}
                    onKeyDown={onKeyDown}
                    placeholder={t('common.write_here')}
                    disabled={disabled}
                    readOnly={disabled}
                    renderPlaceholder={({
                        children,
                        attributes,
                    }: RenderPlaceholderProps) => (
                        <div {...attributes} className={styles.inputPlaceholder}>
                            <p>{children}</p>
                        </div>
                    )}
                    className={styles.input}
                />
                {target && chars.length > 0 && (
                    <Portal>
                        <div
                            ref={ref}
                            className={styles.suggestions}
                            data-cy="mentions-portal"
                        >
                            {chars.map((char, i) => (
                                <div
                                    key={char.id}
                                    className={`${styles.suggestionItem} ${BODY_CLICK_IGNORE_CLASS_NAME}`}
                                    onClick={(e: React.MouseEvent) => {
                                        Transforms.select(editor, target)
                                        insertMention(editor, char.name, char.id)
                                        setTarget(null)
                                    }}
                                    style={{
                                        background: i === index ? '#eef0f3' : 'transparent',
                                    }}
                                >
                                    {char.name}
                                </div>
                            ))}
                        </div>
                    </Portal>
                )}
            </Slate>
        </div>
    );
};

export default forwardRef<ExpressionInputRef, ExpressionInputProps>(ExpressionInput);
